import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import CompassControl from 'mapbox-gl-controls/lib/compass';
import ZoomControl from 'mapbox-gl-controls/lib/zoom';
import 'mapbox-gl-controls/theme.css';
import languageControl from '../../Map/controls/LanguageControl';
import {
  buildingIdMapFilter,
  getColor,
  LAYERS,
  LUN_SOURCE,
  LAYERS_TO_COLORIZE,
  initMapLayers,
} from '../../Map/layers';
import { attachMapAnalytics } from '../../helpers/analytics';
import { getBuildingsDataByIds } from '../../helpers/getBuildingsDataByIds';

class ConstructionMap {
  constructor(parent, sourceData, houseId = 0) {
    this.container = parent.querySelector('#map-container');
    this.wrapper = this.container.parentElement;
    if (!this.container || !mapboxgl.supported()) return;
    const { buildingId } = window.params;
    this.source = sourceData;
    this.house = houseId;
    this.buildingData = [];
    this.buildingId = buildingId;
    this.scrollListener = this.scrollListener.bind(this);
    this.setSections(houseId);
    this.attachScrollListener();
    this.updateMaxHeight();
  }

  updateMaxHeight(galleryNode) {
    if (!this.wrapper || !galleryNode) return;
    const gridNode = galleryNode.querySelector('[data-house-grid]');

    this.wrapper.style.maxHeight = `${gridNode.offsetHeight}px`;
  }

  setSections(houseId) {
    this.sections = this.source[houseId].section_ids;
  }

  fetchBuildingData() {
    getBuildingsDataByIds([this.buildingId], (buildingData) => {
      this.buildingData = buildingData;
      this.colorize();
    });
  }

  attachScrollListener() {
    window.addEventListener('scroll', this.scrollListener);
    this.scrollListener();
  }

  scrollListener() {
    const rect = this.container.getBoundingClientRect();
    const preloadTop = window.innerHeight + 500;
    if (rect.top < preloadTop && rect.bottom >= 0) {
      this.init();
      window.removeEventListener('scroll', this.scrollListener);
    }
  }

  colorize() {
    LAYERS_TO_COLORIZE.forEach((layerId) => {
      const layer = this.map.getLayer(layerId);
      const colorProperty = `${layer.type}-color`;
      this.map.setPaintProperty(
        layerId,
        colorProperty,
        getColor(this.buildingId, {
          selectedSectionIds: this.sections,
          filtered: this.buildingData,
          layerId,
        }),
      );
    });
  }

  init() {
    const { center, bounds } = window.params;
    this.map = new mapboxgl.Map({
      container: this.container,
      style: window.mapStreetsStyle,
      zoom: 8,
      center,
      bounds,
      fitBoundsOptions: {
        maxZoom: 16,
      },
      minZoom: 13,
      maxZoom: 18,
      pitch: 20,
      scrollZoom: false,
      dragPan: !window.isMobile,
      attributionControl: false,
    });
    this.map.addControl(languageControl);
    this.map.addControl(new ZoomControl(), 'bottom-right');
    this.map.addControl(new CompassControl(), 'bottom-right');
    this.fetchBuildingData();
    this.attachListeners();
    attachMapAnalytics(this.container);
  }

  attachListeners() {
    this.map.on('style.load', () => {
      this.selectSections(this.house);
      initMapLayers(this.map);
    });
  }

  flyToHouse(sections) {
    const bounds = new mapboxgl.LngLatBounds();
    const features = this.map.querySourceFeatures(LUN_SOURCE, {
      sourceLayer: LAYERS.sections,
      filter: ['in', buildingIdMapFilter, ['literal', sections]],
    });
    features.forEach((feature) => {
      feature.geometry.coordinates[0].forEach((point) => {
        if (point.length !== 2) return;
        bounds.extend(new mapboxgl.LngLat(point[0], point[1]));
      });
    });

    if (bounds.isEmpty()) return;
    this.map.flyTo({ center: bounds.getCenter() });
  }

  selectSections(houseId) {
    if (!this.map) return;
    this.setSections(houseId);
    this.map.resize();
    this.colorize();
    this.flyToHouse(this.sections);
  }
}

export default ConstructionMap;
